import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Volume2, BellRing, Target, Compass, Clapperboard, FlaskConical, ArrowRight, Gauge, Activity, TrendingUp, ListChecks, MessageSquare, ArrowUpRight, Users, Brain } from "lucide-react";

const GOOD = "#3D5A45", WARN = "#B5704F", BAD = "#A94442";

const useInView = (threshold = 0.3) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
};

const Counter = ({ to, prefix = "", suffix = "", active, ms = 1400 }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf; const t0 = performance.now();
    const step = (t) => { const p = Math.min(1, (t - t0) / ms); setV(Math.round(to * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, to, ms]);
  return <>{prefix}{v.toLocaleString()}{suffix}</>;
};

const fade = (active, i) => ({ opacity: active ? 1 : 0, transform: active ? "none" : "translateY(8px)", transition: `opacity .5s ${i * 0.12}s, transform .5s ${i * 0.12}s` });

const Label = ({ children }) => <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{children}</div>;
const Bullets = ({ title, items, color, active, i }) => (
  <div style={fade(active, i)}>
    <Label>{title}</Label>
    <ul className="space-y-1">{items.map((t) => <li key={t} className="text-[12px] leading-snug flex gap-1.5"><span style={{ color }}>•</span><span>{t}</span></li>)}</ul>
  </div>
);

const BRIEF = {
  happening: ["7 in today · Amira at 11 — it's her birthday", "12 new bookings this week (4 new clients) — mostly Instagram", "$18,420 taken so far this month, $9,300 booked ahead"],
  todo: ["Thursday has 6 empty hours (~$1,900) — 9 clients are due, text them", "Reorder numbing cream — still open from Monday", "Sophie's package expires in 10 days — 2 sessions left"],
  trajectory: ["Revenue +4% vs target-to-date ($17,700)", "Rebooking 68% (was 61%) — holding or improving", "14 open hours over the next 7 days, Thursday is the quietest"],
};

// Mirrors the real Intelligence page: brief → numbers → do next → conversation, in the app's light theme.
const IntelShot = ({ active }) => (
  <div className="rounded-2xl border border-border bg-background shadow-2xl overflow-hidden text-left" data-testid="intel-shot">
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-muted/40">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center"><Sparkles className="h-4 w-4 text-primary" /></div>
        <div><div className="font-heading font-bold text-sm leading-tight">Azordi Intelligence</div><div className="text-[10px] text-muted-foreground">Tuesday 3 October · Vanessa</div></div>
      </div>
      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-border bg-card text-[10px] font-medium" data-testid="intel-shot-read"><Volume2 className="h-3 w-3 text-primary" />Read it to me</span>
    </div>
    <div className="p-4 space-y-4">
      <div>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2"><Activity className="h-3.5 w-3.5 text-primary" />Today's brief</div>
        <div className="rounded-xl border border-border bg-card p-3 grid sm:grid-cols-3 gap-3" data-testid="intel-typewriter">
          <Bullets title="What's happening" items={BRIEF.happening} color={GOOD} active={active} i={0} />
          <Bullets title="What needs doing" items={BRIEF.todo} color={WARN} active={active} i={1} />
          <Bullets title="Trajectory" items={BRIEF.trajectory} color="hsl(var(--primary))" active={active} i={2} />
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2"><TrendingUp className="h-3.5 w-3.5 text-primary" />The numbers</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["Revenue MTD", 18420, "$", "", "+4% vs target", GOOD], ["Vs last month", 9, "+", "%", "same point", GOOD], ["Rebook rate", 68, "", "%", "was 61%", GOOD], ["Open · 7 days", 14, "", "h", "Thu ≈ $1,900 unsold", WARN]].map(([l, to, pre, suf, d, c], i) => (
            <div key={l} className="rounded-xl border border-border bg-card p-2.5 relative overflow-hidden" style={fade(active, i + 3)}>
              <div className="absolute inset-y-0 left-0 w-1" style={{ background: c }} />
              <div className="font-mono-label text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
              <div className="font-heading font-bold text-base tabular-nums"><Counter to={to} prefix={pre} suffix={suf} active={active} /></div>
              <div className="text-[9px]" style={{ color: c }}>{d}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold mb-2"><ListChecks className="h-3.5 w-3.5 text-primary" />Do next</div>
          <div className="space-y-1.5">
            {[["01", "Text the 9 clients due for Thursday", BAD], ["02", "Call Sophie about her 2 unused sessions", WARN]].map(([n, t, c], i) => (
              <div key={n} className="rounded-xl border border-border bg-card p-2.5 flex items-start gap-2" style={fade(active, i + 7)}>
                <span className="font-heading font-bold text-sm tabular-nums" style={{ color: c }}>{n}</span>
                <div className="min-w-0"><div className="text-[11px] font-medium leading-snug">{t}</div><div className="text-[9px] text-primary inline-flex items-center gap-0.5 mt-0.5">Take me there <ArrowUpRight className="h-2.5 w-2.5" /></div></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold mb-2"><MessageSquare className="h-3.5 w-3.5 text-primary" />Conversations</div>
          <div className="rounded-xl border border-border bg-card p-2.5 space-y-1.5" style={fade(active, 9)}>
            <div className="ml-auto w-fit max-w-[90%] bg-primary text-primary-foreground rounded-2xl px-2.5 py-1.5 text-[10px]">How did last week compare to the week before?</div>
            <div className="w-fit max-w-[92%] bg-muted rounded-2xl px-2.5 py-1.5 text-[10px]">Up 11% — $14,200 vs $12,800. Thursday carried it. Want me to text the clients still due?</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PILLARS = [
  { icon: Activity, title: "Today's brief, in points", body: "What's happening, what needs doing, and where you're heading — read it, or tap to have it read to you." },
  { icon: Gauge, title: "Capacity, live", body: "Booked % for the week and month — and the dollars sitting in empty hours." },
  { icon: BellRing, title: "Daily digest + Sunday wrap", body: "Quiet days, no-show spikes, low stock, lapsed VIPs — once a day, with a why. Sunday evening, the week wrapped up." },
  { icon: Target, title: "Names, not charts", body: "Clients due, unused packages and credit — text the right ones in one tap." },
  { icon: MessageSquare, title: "Conversations that act", body: "Saved threads that remember last week. Ask, then let it book, text or add a task — with a tap to confirm." },
  { icon: Users, title: "CRM built in", body: "New, Active, Lapsed and VIP segments, team follow-ups and win-back texts — right under the brief." },
  { icon: FlaskConical, title: "What-if with real wages", body: "Ask in a conversation: hire? raise a price? spend on ads? Projected from your actual costs." },
  { icon: Compass, title: "A coach per therapist", body: "Each person sees only their own numbers and next week's gaps. No leaderboard." },
  { icon: Clapperboard, title: "Content that fills chairs", body: "Reel ideas from your data, each with a tracked link back to bookings." },
];

export const IntelligenceSection = ({ cta }) => {
  const [ref, active] = useInView(0.2);
  const [pillRef, pillIn] = useInView(0.15);
  return (
    <section id="intelligence" ref={ref} className="relative overflow-hidden border-y border-border bg-muted/30" data-testid="landing-intelligence">
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="grid lg:grid-cols-[5fr_7fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="font-mono-label text-[11px] uppercase tracking-widest text-muted-foreground">Azordi Intelligence</span>
            </div>
            <h2 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.05]">A partner who knows every number — and says so.</h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              It watches your clinic, briefs you each morning in plain points, and hands you the next move with the clients' names already on it. <span className="text-primary font-medium">Including what you're doing well.</span>
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to={cta.to}><Button size="lg" className="rounded-lg h-12 px-6" data-testid="landing-cta-intelligence">{cta.label} <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
              <span className="text-sm text-muted-foreground">Every figure is yours. It never invents a number.</span>
            </div>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute -inset-6 rounded-full bg-primary/10 blur-3xl -z-10" />
            <IntelShot active={active} />
          </div>
        </div>

        <div ref={pillRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-16" data-testid="intel-pillars">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:-translate-y-1 hover:shadow-md transition-[border-color,transform,box-shadow] duration-300"
              style={{ opacity: pillIn ? 1 : 0, transform: pillIn ? "none" : "translateY(18px)", transition: `opacity .6s ${i * 0.07}s ease, transform .6s ${i * 0.07}s ease` }} data-testid={`intel-pillar-${i}`}>
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center mb-3"><p.icon className="h-[18px] w-[18px] text-primary" /></div>
              <div className="font-heading font-semibold">{p.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-dashed border-border p-5 mt-4 flex items-start gap-3" style={{ opacity: pillIn ? 1 : 0, transition: `opacity .6s ${PILLARS.length * 0.07}s ease` }}>
          <Brain className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-semibold">It remembers, keeps score honestly, and explains itself.</div>
            <p className="text-sm text-muted-foreground mt-1">Tell it once in Business Memory — “no discount posts” — and every conversation and suggestion respects it. Every number has a “Why?”.</p>
          </div>
        </div>
      </div>
    </section>
  );
};
